/* ==========================================================================
   Bold Movement — Inline Bundle Upsell
   Mobile-first interactive bundle composer that appears when qty >= 2.

   Behavior:
   - Main qty stepper bumps to 2+ → upsell slides down, smart mix pre-fills
   - Per-color +/- steppers let user adjust individual color counts
   - Total qty = sum of color counts (kept in sync with main qty input)
   - "Reset to Smart Pick" re-runs the auto-balance based on viewed color
   - Bundle routes adaptively: largest-fits-first decomposition
   ========================================================================== */

(function () {
  'use strict';

  var upsell = document.querySelector('[data-pdp-upsell]');
  var qtyInput = document.querySelector('[data-pdp-qty]');
  var configEl = document.getElementById('bm-pdp-upsell-config');
  if (!upsell || !qtyInput || !configEl) return;

  var config;
  try { config = JSON.parse(configEl.textContent); } catch (e) { return; }
  if (!config.tiers || !config.tiers.length) return;

  config.tiers.forEach(function (t) {
    if (typeof t.price === 'string') t.price = parseInt(t.price, 10);
    if (isNaN(t.price)) t.price = 0;
  });
  var sortedTiers = config.tiers.slice().sort(function (a, b) { return b.size - a.size; });

  var SOLO_CENTS = (function () {
    var s = config.tiers.find(function (t) { return t.size === 1; });
    return s && s.price ? s.price : 2500;
  })();

  var COLOR_NAMES = { aegean: 'Aegean Blue', onyx: 'Onyx Black', artemis: 'Artemis Red' };
  var COLOR_ORDER = ['aegean', 'onyx', 'artemis'];

  var state = {
    mix: { aegean: 0, onyx: 0, artemis: 0 },
    anchor: config.currentColor || 'aegean',
    userTouched: false
  };

  /* ---- math --------------------------------------------------------- */

  function money(cents) {
    var n = cents / 100;
    return '$' + (Math.round(n * 100) / 100).toFixed(2).replace(/\.00$/, '');
  }
  function moneyExact(cents) { return '$' + (cents / 100).toFixed(2); }

  function totalQty() {
    return state.mix.aegean + state.mix.onyx + state.mix.artemis;
  }

  function decompose(n) {
    var out = [];
    var remaining = n;
    sortedTiers.forEach(function (t) {
      while (remaining >= t.size) {
        out.push(t);
        remaining -= t.size;
      }
    });
    return out;
  }

  function smartMix(anchor, totalPairs) {
    var mix = { aegean: 0, onyx: 0, artemis: 0 };
    if (totalPairs <= 0) return mix;

    // Always include one of the anchor color (the viewed colorway)
    mix[anchor] = 1;
    var remaining = totalPairs - 1;
    if (remaining <= 0) return mix;

    // Distribute the rest others-first so 2 pairs = 1 anchor + 1 other (not 2 of anchor)
    var others = COLOR_ORDER.filter(function (c) { return c !== anchor; });
    var sequence = [others[0], others[1], anchor];
    var idx = 0;
    while (remaining > 0) {
      mix[sequence[idx % sequence.length]] += 1;
      remaining -= 1;
      idx += 1;
    }
    return mix;
  }

  function planLabel(parts) {
    if (!parts.length) return '';
    if (parts.length === 1) return parts[0].size === 1 ? 'Solo' : parts[0].size + '-Pack';
    var counts = {};
    parts.forEach(function (p) { counts[p.size] = (counts[p.size] || 0) + 1; });
    return Object.keys(counts).map(Number).sort(function (a, b) { return b - a; }).map(function (size) {
      var c = counts[size];
      var name = size === 1 ? 'Solo' : size + '-Pack';
      return c > 1 ? c + '× ' + name : name;
    }).join(' + ');
  }

  /* ---- DOM refs ----------------------------------------------------- */

  var $chip       = upsell.querySelector('[data-upsell-chip]');
  var $save       = upsell.querySelector('[data-upsell-save]');
  var $intro      = upsell.querySelector('[data-upsell-intro]');
  var $was        = upsell.querySelector('[data-upsell-was]');
  var $now        = upsell.querySelector('[data-upsell-now]');
  var $final      = upsell.querySelector('[data-upsell-final]');
  var $atc        = upsell.querySelector('[data-pdp-upsell-atc]');
  var $atcLabel   = upsell.querySelector('[data-upsell-atc-label]');
  var $fallbackQty = upsell.querySelector('[data-upsell-fallback-qty]');
  var $total      = upsell.querySelector('[data-upsell-total]');
  var $reset      = upsell.querySelector('[data-upsell-reset]');

  function setCount(color, n) {
    var el = upsell.querySelector('[data-upsell-count="' + color + '"]');
    if (el) {
      if (el.textContent !== String(n)) {
        el.textContent = n;
        el.classList.remove('is-bumped');
        // Reflow so transition fires
        void el.offsetWidth;
        el.classList.add('is-bumped');
      }
    }
    var row = upsell.querySelector('[data-color-row="' + color + '"]');
    if (row) row.classList.toggle('has-count', n > 0);
    var minusBtn = upsell.querySelector('[data-color-minus="' + color + '"]');
    if (minusBtn) minusBtn.disabled = n === 0;
  }

  /* ---- render -------------------------------------------------------- */

  function render() {
    var n = totalQty();

    if (n < 1) {
      // No pairs — hide upsell entirely
      hide();
      return;
    }
    if (n < 2) {
      // Only 1 pair — bundles don't apply, hide upsell
      hide();
      return;
    }

    show();

    var parts = decompose(n);
    var bundleSubtotal = parts.reduce(function (s, p) { return s + p.price; }, 0);
    var soloCompare = n * SOLO_CENTS;
    var saved = Math.max(0, soloCompare - bundleSubtotal);
    var finalAfter = Math.round(bundleSubtotal * (1 - (config.discountPercent || 0) / 100));

    if ($chip) $chip.textContent = 'Smart Pick · ' + planLabel(parts);
    if ($save) {
      if (saved > 0) { $save.textContent = 'Save ' + moneyExact(saved); $save.hidden = false; }
      else { $save.hidden = true; }
    }
    if ($intro) {
      if (state.userTouched) {
        $intro.textContent = 'Your mix — total updates automatically.';
      } else if (parts.length === 1 && parts[0].size === n) {
        $intro.textContent = 'Most-loved ' + n + '-pack mix — adjust if you like.';
      } else {
        $intro.textContent = 'Best-value combo: ' + planLabel(parts) + '. Tap +/− to adjust.';
      }
    }
    if ($was) {
      if (saved > 0) { $was.textContent = money(soloCompare); $was.hidden = false; }
      else { $was.hidden = true; }
    }
    if ($now) $now.textContent = money(bundleSubtotal);
    if ($final) $final.textContent = money(finalAfter);
    if ($atcLabel) $atcLabel.textContent = 'Add ' + planLabel(parts) + ' — ' + money(bundleSubtotal) + ' →';
    if ($fallbackQty) $fallbackQty.textContent = n;
    if ($total) $total.textContent = n;

    COLOR_ORDER.forEach(function (c) { setCount(c, state.mix[c]); });

    // Sync main qty input to total (silently — no event)
    if (qtyInput && parseInt(qtyInput.value, 10) !== n) {
      qtyInput.value = n;
    }
  }

  /* ---- show / hide with smooth animation ---------------------------- */

  function show() {
    if (!upsell.hidden) return;
    upsell.hidden = false;
    void upsell.offsetHeight;
    requestAnimationFrame(function () { upsell.classList.add('is-visible'); });
  }
  function hide() {
    if (upsell.hidden) return;
    upsell.classList.remove('is-visible');
    var onEnd = function (e) {
      if (e.target !== upsell) return;
      upsell.hidden = true;
      upsell.removeEventListener('transitionend', onEnd);
    };
    upsell.addEventListener('transitionend', onEnd);
    setTimeout(function () {
      if (!upsell.classList.contains('is-visible')) upsell.hidden = true;
    }, 500);
  }

  /* ---- main qty input → triggers smart mix + render ----------------- */

  function syncFromMainQty() {
    var raw = parseInt(qtyInput.value, 10);
    if (isNaN(raw) || raw < 0) raw = 0;
    if (raw > 99) raw = 99;

    if (raw < 2) {
      // Going below bundle threshold — clear state
      state.mix = { aegean: 0, onyx: 0, artemis: 0 };
      state.userTouched = false;
    } else {
      // If user hasn't manually edited colors OR total mismatches main qty,
      // run smart mix fresh.
      if (!state.userTouched || totalQty() !== raw) {
        state.mix = smartMix(state.anchor, raw);
      }
    }
    render();
  }

  qtyInput.addEventListener('input', syncFromMainQty);
  qtyInput.addEventListener('change', syncFromMainQty);

  // bm-pdp.js binds +/- on the main stepper to manipulate qtyInput.
  // Watch after their handler runs (rAF tick).
  document.querySelectorAll('[data-pdp-qty-plus], [data-pdp-qty-minus]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      requestAnimationFrame(syncFromMainQty);
    });
  });

  /* ---- per-color steppers ------------------------------------------- */

  upsell.querySelectorAll('[data-color-plus]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      var color = btn.getAttribute('data-color-plus');
      if (!(color in state.mix)) return;
      if (totalQty() >= 99) return;
      state.mix[color] += 1;
      state.userTouched = true;
      render();
    });
  });
  upsell.querySelectorAll('[data-color-minus]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      var color = btn.getAttribute('data-color-minus');
      if (!(color in state.mix)) return;
      if (state.mix[color] <= 0) return;
      state.mix[color] -= 1;
      state.userTouched = true;
      render();
    });
  });

  /* ---- reset to smart pick ----------------------------------------- */

  if ($reset) {
    $reset.addEventListener('click', function () {
      var n = totalQty();
      if (n < 2) n = 2; // default to 2 if user emptied it
      state.mix = smartMix(state.anchor, n);
      state.userTouched = false;
      render();
    });
  }

  /* ---- skip bundle: buy N of viewed color via main form ------------ */

  var $skip = upsell.querySelector('[data-upsell-skip]');
  if ($skip) {
    $skip.addEventListener('click', function () {
      var mainAtc = document.querySelector('.bm-buy__atc[data-pdp-atc]');
      if (mainAtc && mainAtc.form) {
        if (typeof mainAtc.form.requestSubmit === 'function') {
          mainAtc.form.requestSubmit(mainAtc);
        } else {
          mainAtc.form.submit();
        }
      }
    });
  }

  /* ---- bundle ATC --------------------------------------------------- */

  function buildCartItems() {
    var n = totalQty();
    var parts = decompose(n);
    var pool = [];
    COLOR_ORDER.forEach(function (c) {
      for (var i = 0; i < state.mix[c]; i++) pool.push(c);
    });

    var items = [];
    parts.forEach(function (part) {
      var take = pool.splice(0, part.size);
      var local = {};
      take.forEach(function (c) { local[c] = (local[c] || 0) + 1; });

      if (part.size === 1) {
        var color = Object.keys(local)[0];
        var variantId = config.soloVariants && config.soloVariants[color];
        if (variantId) items.push({ id: Number(variantId), quantity: 1 });
      } else {
        if (!part.variantId) return;
        var colorString = COLOR_ORDER
          .filter(function (c) { return local[c] > 0; })
          .map(function (c) { return local[c] + '× ' + COLOR_NAMES[c]; })
          .join(' · ');
        items.push({
          id: Number(part.variantId),
          quantity: 1,
          properties: { 'Colors': colorString, 'Bundle': part.size + '-pack' }
        });
      }
    });
    return items;
  }

  function addBundle() {
    var items = buildCartItems();
    if (!items.length) {
      alert('Bundle products are not connected yet. Connect them in Theme editor → BM Product Killer → Inline Bundle Upsell.');
      return;
    }

    var origLabel = $atcLabel ? $atcLabel.textContent : '';
    $atc.disabled = true;
    if ($atcLabel) $atcLabel.textContent = 'Adding…';

    var fd = new FormData();
    items.forEach(function (it, i) {
      fd.append('items[' + i + '][id]', it.id);
      fd.append('items[' + i + '][quantity]', it.quantity);
      if (it.properties) {
        Object.keys(it.properties).forEach(function (k) {
          fd.append('items[' + i + '][properties][' + k + ']', it.properties[k]);
        });
      }
    });
    fd.append('sections', 'cart-drawer,cart-icon-bubble');

    fetch('/cart/add.js', {
      method: 'POST',
      body: fd,
      headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
    }).then(function (r) {
      if (!r.ok) throw new Error('add');
      return r.json();
    }).then(function (resp) {
      var drawer = document.querySelector('cart-drawer');
      if (drawer && resp.sections && resp.sections['cart-drawer']) {
        var parser = new DOMParser();
        var doc = parser.parseFromString(resp.sections['cart-drawer'], 'text/html');
        var newCD = doc.querySelector('cart-drawer');
        var newInner = newCD ? newCD.querySelector('#CartDrawer') : doc.querySelector('#CartDrawer');
        var oldInner = drawer.querySelector('#CartDrawer');
        if (newInner && oldInner) oldInner.replaceWith(newInner);
        drawer.classList.remove('is-empty');
      }
      if (resp.sections && resp.sections['cart-icon-bubble']) {
        var bubble = document.querySelector('#cart-icon-bubble');
        if (bubble) bubble.innerHTML = resp.sections['cart-icon-bubble'];
      }
      $atc.classList.add('is-added');
      if ($atcLabel) $atcLabel.textContent = '✓ Added';
      if (drawer && typeof drawer.open === 'function') drawer.open();

      setTimeout(function () {
        $atc.disabled = false;
        $atc.classList.remove('is-added');
        if ($atcLabel) $atcLabel.textContent = origLabel;
      }, 2200);
    }).catch(function () {
      if ($atcLabel) $atcLabel.textContent = 'Retry';
      $atc.disabled = false;
    });
  }

  $atc.addEventListener('click', function (e) {
    e.preventDefault();
    addBundle();
  });

  /* ---- init --------------------------------------------------------- */

  syncFromMainQty();
})();
